import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { usePortal, usePortalPath } from '../../contexts/PortalContext';
import { resolveNotificationLink } from '../../lib/reminderNotifications';
import { useCrmData } from '../../contexts/CrmDataContext';
import { useCrmPageNotifications } from '../../contexts/CrmPageNotificationsContext';
import { useMaxWidth } from '../../hooks/useMaxWidth';
import { approveAccountTransfer, rejectAccountTransfer } from '../../lib/accountTransfer';

import { NotificationBellIcon } from './NotificationBellIcon';
import { NotificationSetupCard } from './NotificationSetupCard';
import type { NotificationSetupState } from '../../lib/notificationSetup';

type PanelPos = {
  top: number;
  maxHeight: number;
};

type AbovePanelPos = {
  left: number;
  bottom: number;
  width: number;
  maxHeight: number;
};

function panelMaxHeight(anchorBottom: number): number {
  const dockGap = 100;
  const reserved = dockGap + 16;
  return Math.max(180, Math.min(420, window.innerHeight - anchorBottom - reserved));
}

export function NotificationsBell({
  setupState,
  onEnableNotifications,
  webPushConfigured,
  placement = 'below',
}: {
  setupState: NotificationSetupState;
  onEnableNotifications: () => void;
  webPushConfigured: boolean;
  /** Open above the bell (sidebar footer). Default: below. */
  placement?: 'below' | 'above';
}) {
  const approvalsPath = usePortalPath('approvals');
  const { basePath } = usePortal();
  const navigate = useNavigate();
  const isMobile = useMaxWidth(900);
  const {
    notifications,
    unreadNotificationCount,
    canApproveTransfers,
    markNotificationRead,
    markAllNotificationsRead,
    refresh,
  } = useCrmData();
  const { push: pushToast } = useCrmPageNotifications();
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [panelPos, setPanelPos] = useState<PanelPos>({ top: 56, maxHeight: 320 });
  const [abovePos, setAbovePos] = useState<AbovePanelPos>({
    left: 12,
    bottom: 80,
    width: 320,
    maxHeight: 360,
  });
  const rootRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const portalAbove = placement === 'above' && !isMobile;

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;

    const update = () => {
      const rect = btnRef.current!.getBoundingClientRect();
      if (portalAbove) {
        const width = Math.min(320, window.innerWidth - 24);
        let left = rect.right - width;
        if (left < 12) left = 12;
        if (left + width > window.innerWidth - 12) {
          left = Math.max(12, window.innerWidth - width - 12);
        }
        const maxHeight = Math.max(160, Math.min(360, rect.top - 24));
        setAbovePos({
          left,
          bottom: window.innerHeight - rect.top + 10,
          width,
          maxHeight,
        });
        return;
      }
      if (!isMobile) return;
      const top = rect.bottom + 8;
      setPanelPos({ top, maxHeight: panelMaxHeight(top) });
    };

    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, isMobile, portalAbove]);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      const target = e.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  async function handleOpen(n: { id: string; link?: string | null }) {
    await markNotificationRead(n.id);
    setOpen(false);
    if (n.link) {
      const target = resolveNotificationLink(n.link, basePath);
      const hashIndex = target.indexOf('#');
      if (hashIndex >= 0) {
        navigate({ pathname: target.slice(0, hashIndex), hash: target.slice(hashIndex) });
      } else {
        navigate(target);
      }
    }
  }

  async function handleApprove(requestId: string, notificationId: string) {
    setBusyId(requestId);
    try {
      await approveAccountTransfer(requestId);
      await markNotificationRead(notificationId);
      await refresh({ silent: true, slices: ['transferRequests', 'notifications'] });
    } catch (err) {
      pushToast({
        tone: 'warn',
        title: 'Approve failed',
        lines: [err instanceof Error ? err.message : 'Approve failed.'],
      });
    } finally {
      setBusyId(null);
    }
  }

  async function handleReject(requestId: string, notificationId: string) {
    const note = window.prompt('Rejection reason (optional):') ?? '';
    setBusyId(requestId);
    try {
      await rejectAccountTransfer(requestId, note);
      await markNotificationRead(notificationId);
      await refresh({ silent: true, slices: ['transferRequests', 'notifications'] });
    } catch (err) {
      pushToast({
        tone: 'warn',
        title: 'Reject failed',
        lines: [err instanceof Error ? err.message : 'Reject failed.'],
      });
    } finally {
      setBusyId(null);
    }
  }

  const panel = open ? (
    <div
      ref={panelRef}
      className={[
        'notifications-bell__panel',
        isMobile ? 'notifications-bell__panel--mobile' : '',
        portalAbove ? 'notifications-bell__panel--above notifications-bell__panel--fixed-above' : '',
        !isMobile && placement === 'above' && !portalAbove ? 'notifications-bell__panel--above' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      role="menu"
      style={
        portalAbove
          ? {
              position: 'fixed',
              left: abovePos.left,
              bottom: abovePos.bottom,
              top: 'auto',
              right: 'auto',
              width: abovePos.width,
              maxWidth: abovePos.width,
              maxHeight: abovePos.maxHeight,
            }
          : isMobile
            ? {
                top: panelPos.top,
                maxHeight: panelPos.maxHeight,
              }
            : undefined
      }
    >
      <div className="notifications-bell__panel-head">
        <strong>Notifications</strong>
        {unreadNotificationCount > 0 && (
          <button
            type="button"
            className="account-side-btn account-side-btn--muted"
            onClick={() => void markAllNotificationsRead()}
          >
            Mark all read
          </button>
        )}
      </div>

      <NotificationSetupCard
        setupState={setupState}
        onEnable={onEnableNotifications}
        webPushConfigured={webPushConfigured}
      />

      {canApproveTransfers && (
        <Link to={approvalsPath} className="notifications-bell__approvals-link" onClick={() => setOpen(false)}>
          Pending transfer approvals →
        </Link>
      )}

      {notifications.length === 0 ? (
        <p className="account-side-muted notifications-bell__empty">No notifications yet.</p>
      ) : (
        <ul className="notifications-bell__list">
          {notifications.slice(0, 20).map((n) => {
            const isUnread = !n.read_at;
            const canAct =
              canApproveTransfers &&
              n.kind === 'transfer_submitted' &&
              n.related_request_id &&
              isUnread;
            return (
              <li
                key={n.id}
                className={`notifications-bell__item${isUnread ? ' notifications-bell__item--unread' : ''}`}
              >
                <button
                  type="button"
                  className="notifications-bell__item-body"
                  onClick={() => void handleOpen(n)}
                >
                  <strong>{n.title}</strong>
                  <span>{n.body}</span>
                  {n.created_at && (
                    <time className="notifications-bell__time">{n.created_at.slice(0, 16).replace('T', ' ')}</time>
                  )}
                </button>
                {canAct && n.related_request_id && (
                  <div className="notifications-bell__actions">
                    <button
                      type="button"
                      className="btn-primary btn-sm"
                      disabled={busyId === n.related_request_id}
                      onClick={() => void handleApprove(n.related_request_id!, n.id)}
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      disabled={busyId === n.related_request_id}
                      onClick={() => void handleReject(n.related_request_id!, n.id)}
                    >
                      Reject
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  ) : null;

  return (
    <div className="notifications-bell" ref={rootRef}>
      <button
        ref={btnRef}
        type="button"
        className="btn-secondary btn-sm notifications-bell__btn"
        aria-expanded={open}
        aria-label={`Notifications${unreadNotificationCount ? `, ${unreadNotificationCount} unread` : ''}`}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="notifications-bell__icon" aria-hidden>
          <NotificationBellIcon />
        </span>
        {unreadNotificationCount > 0 && (
          <span className="notifications-bell__badge">
            {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
          </span>
        )}
      </button>

      {(isMobile || portalAbove) && panel ? createPortal(panel, document.body) : panel}
    </div>
  );
}
